if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        this.setupVariantChangeListener();
      }

      setupVariantChangeListener() {
        // Utiliser le système PubSub de Shopify
        if (typeof subscribe !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, () => {
            // Petit délai pour laisser le DOM se mettre à jour
            setTimeout(() => {
              this.fixMediaContainers();
            }, 100);
          });
        }
        
        // Fallback : observer les mutations DOM
        const observer = new MutationObserver(() => {
          this.fixMediaContainers();
        });
        
        observer.observe(this.modalContent, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style']
        });
      }

      fixMediaContainers() {
        const mediaContainers = this.modalContent.querySelectorAll('.product-media-container.constrain-height');
        if (mediaContainers.length === 0) return;
        
        mediaContainers.forEach(container => {
          const ratio = container.style.getPropertyValue('--ratio') || '1.0';
          
          container.style.setProperty('--ratio-percent', `calc(1 / ${ratio} * 100%)`);
          container.style.setProperty('--aspect-ratio', ratio);
          container.style.setProperty('--constrained-min-height', '400px');
          container.style.setProperty('--modal-height-offset', '10rem');
          container.style.setProperty('--modal-padding', '0px');
          container.style.setProperty('--popup-border-width', '0px');
          container.style.setProperty('--viewport-offset', 'calc((var(--modal-height-offset) + var(--modal-padding) + var(--popup-border-width)) * 2)');
          container.style.setProperty('--constrained-height', 'max(var(--constrained-min-height), calc(100vh - var(--viewport-offset)))');
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        if (opener.querySelector('.loading__spinner')) {
          opener.querySelector('.loading__spinner').classList.remove('hidden');
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (opener.querySelector('.loading__spinner')) {
              opener.querySelector('.loading__spinner').classList.add('hidden');
            }
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        const mediaContainers = productElement.querySelectorAll('.product-media-container.constrain-height');
        mediaContainers.forEach(container => {
          const ratio = container.style.getPropertyValue('--ratio') || '1.0';
          
          // Calculer ratio-percent
          container.style.setProperty('--ratio-percent', `calc(1 / ${ratio} * 100%)`);
          container.style.setProperty('--aspect-ratio', ratio);
          
          // Définir les variables de contrainte de hauteur
          container.style.setProperty('--constrained-min-height', '400px');
          container.style.setProperty('--modal-height-offset', '10rem');
          container.style.setProperty('--modal-padding', '0px');
          container.style.setProperty('--popup-border-width', '0px');
          
          // Calculer viewport-offset
          container.style.setProperty('--viewport-offset', 'calc((var(--modal-height-offset) + var(--modal-padding) + var(--popup-border-width)) * 2)');
          
          // Calculer constrained-height
          container.style.setProperty('--constrained-height', 'max(var(--constrained-min-height), calc(100vh - var(--viewport-offset)))');
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.addSellingPlanSelect(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      // ====== 🧩 Injection du select selling_plan dans la quick add modal ======
      addSellingPlanSelect(productElement) {
        try {
          const form = productElement.querySelector('product-form form.form');
          if (!form) return;

          // Ne pas dupliquer si déjà présent
          if (form.querySelector('[name="selling_plan"]')) return;

          // Récup JSON produit
          const jsonEl = productElement.querySelector('.jsonProduct');
          if (!jsonEl) return;

          const productData = JSON.parse(jsonEl.textContent || '{}');
          const groups = Array.isArray(productData.selling_plan_groups) 
            ? productData.selling_plan_groups 
            : [];

          const requires = !!productData.requires_selling_plan || groups.length > 0;

          if (!requires || groups.length === 0) return;

          // Aplatir tous les plans
          const plans = [];
          groups.forEach((g) => {
            (g.selling_plans || []).forEach((sp) => plans.push(sp));
          });

          if (plans.length === 0) return;

          // Plan par défaut: "Tous les Mois" en priorité
          const preferred = plans.find((p) => /tous\s+les\s+mois/i.test(p.name)) || plans[0];

          // ✅ Cibler le conteneur .product-form__buttons
          const buttonsContainer = form.querySelector('.product-form__buttons');
          const submitBtn = form.querySelector('button[type="submit"]');
          const quantityDiv = form.querySelector('.product-form__quantity');

          if (!buttonsContainer) return;

          // ✅ Appliquer flex-direction: column au container principal
          buttonsContainer.style.display = 'flex';
          buttonsContainer.style.flexDirection = 'column';
          buttonsContainer.style.gap = '1rem';
          buttonsContainer.style.alignItems = 'flex-start';

          if (plans.length > 1) {
            // ✅ Créer le label séparé
            const label = document.createElement('label');
            label.className = 'form__label fw-600';
            const selId = `SellingPlan-${Date.now()}`;
            label.setAttribute('for', selId);
            label.textContent = 'Fréquence de Livraison';
            label.style.lineHeight = '1.4';
            label.style.fontSize = '1.6rem';
            label.style.fontWeight = '600';

            // ✅ Créer le select
            const select = document.createElement('select');
            select.name = 'selling_plan';
            select.id = selId;
            select.className = 'select-selector';
            select.required = true;
            select.style.width = '100%';

            plans.forEach((p) => {
              const opt = document.createElement('option');
              opt.value = p.id;
              opt.textContent = p.name;
              if (p.id === preferred.id) opt.selected = true;
              select.appendChild(opt);
            });

            // ✅ Créer un wrapper pour le select + icône
            const selectWrapper = document.createElement('div');
            selectWrapper.className = 'selectus';
            selectWrapper.style.position = 'relative';
            selectWrapper.style.width = '100%';
            selectWrapper.style.maxWidth = '200px';
            selectWrapper.style.marginBottom = '1rem';

            // ✅ Créer l'icône SVG
            const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            iconSvg.setAttribute('aria-hidden', 'true');
            iconSvg.setAttribute('focusable', 'false');
            iconSvg.setAttribute('class', 'icon icon-caret');
            iconSvg.setAttribute('viewBox', '0 0 10 6');

            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('fill-rule', 'evenodd');
            path.setAttribute('clip-rule', 'evenodd');
            path.setAttribute('d', 'M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z');
            path.setAttribute('fill', 'currentColor');

            iconSvg.appendChild(path);

            // ✅ Assembler select + icône dans le wrapper
            selectWrapper.appendChild(select);
            selectWrapper.appendChild(iconSvg);

            // ✅ Créer un wrapper pour Quantité + Bouton (en row)
            const rowWrapper = document.createElement('div');
            rowWrapper.className = 'product-form__row';
            rowWrapper.style.display = 'flex';
            rowWrapper.style.gap = '1rem';
            rowWrapper.style.alignItems = 'flex-end';

            // ✅ Insérer dans l'ordre :
            // 1. Label en premier
            buttonsContainer.insertBefore(label, buttonsContainer.firstElementChild);
            
            // 2. Select wrapper (select + icône) en deuxième
            buttonsContainer.insertBefore(selectWrapper, buttonsContainer.children[1]);
            
            // 3. Déplacer quantité et bouton dans le rowWrapper
            if (quantityDiv) {
              rowWrapper.appendChild(quantityDiv);
              quantityDiv.style.flex = '1';
            }
            
            if (submitBtn) {
              rowWrapper.appendChild(submitBtn);
              submitBtn.style.flex = '2';
            }
            
            // 4. Ajouter le rowWrapper
            buttonsContainer.appendChild(rowWrapper);
          } else {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = 'selling_plan';
            hidden.value = preferred.id;
            form.insertBefore(hidden, buttonsContainer);
          }
        } catch (e) {
          console.warn('[QuickAddModal] addSellingPlanSelect error:', e);
        }
      }
    }
  );
}